// src/routes/OpsDashboardRoute.tsx
import React from "react";

import type { OpsDashboardStateV1 } from "@/contracts/ops/OpsDashboardModel";
import { subscribe, getState, installOpsStateBus } from "@/contracts/ops/OpsStateBus";
import { installWiring } from "@/wiring/installWiring";
import { SYNK_OPS_SNAPSHOT_REQUEST_EVENT } from "@/contracts/ops/OpsEvents";

/* ------------------------------------------------------------
 * UI primitives (glass + pill, no page scroll)
 * ---------------------------------------------------------- */

type Tone = "quiet" | "normal" | "ok" | "warn" | "crit";

function toneCls(t: Tone) {
  switch (t) {
    case "crit":
      return "bg-red-500/12 text-red-200 border-red-500/25 dark:bg-red-500/12 dark:text-red-200";
    case "warn":
      return "bg-yellow-400/12 text-yellow-200 border-yellow-400/25 dark:bg-yellow-400/12 dark:text-yellow-200";
    case "ok":
      return "bg-emerald-400/10 text-emerald-200 border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-200";
    case "quiet":
      return "bg-white/6 text-white/55 border-white/10 dark:bg-white/6 dark:text-white/55";
    default:
      return "bg-white/10 text-white/85 border-white/15 dark:bg-white/10 dark:text-white/85";
  }
}

function Pill(props: {
  children: React.ReactNode;
  tone?: Tone;
  icon?: React.ReactNode;
  title?: string;
}) {
  const t = props.tone ?? "normal";
  return (
    <span
      title={props.title}
      className={[
        "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border",
        "backdrop-blur-md",
        toneCls(t),
      ].join(" ")}
    >
      {props.icon ? <span className="opacity-90">{props.icon}</span> : null}
      <span className="whitespace-nowrap">{props.children}</span>
    </span>
  );
}

function Panel(props: {
  title: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={[
        "rounded-2xl border border-white/10 bg-white/[0.06] dark:bg-white/[0.06]",
        "backdrop-blur-xl shadow-[0_18px_60px_rgba(0,0,0,0.35)]",
        "overflow-hidden",
        props.className ?? "",
      ].join(" ")}
    >
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold tracking-tight">{props.title}</div>
        {props.right ? <div className="shrink-0">{props.right}</div> : null}
      </div>
      <div className="p-4">{props.children}</div>
    </section>
  );
}

function IconDot(props: { tone: Tone }) {
  const cls =
    props.tone === "crit"
      ? "bg-red-400"
      : props.tone === "warn"
      ? "bg-yellow-300"
      : props.tone === "ok"
      ? "bg-emerald-300"
      : props.tone === "quiet"
      ? "bg-white/35"
      : "bg-white/70";
  return <span className={`inline-block size-2 rounded-full ${cls}`} />;
}

function bandTone(band?: string): Tone {
  if (band === "critical") return "crit";
  if (band === "warning") return "warn";
  if (band === "normal") return "ok";
  return "quiet";
}
function healthTone(h?: string): Tone {
  if (h === "faulted" || h === "blocked") return "crit";
  if (h === "degraded") return "warn";
  if (h === "ok") return "ok";
  return "quiet";
}

function shortId(s?: string, n = 10): string {
  const x = String(s ?? "").trim();
  if (!x) return "unknown";
  return x.length <= n ? x : `${x.slice(0, n)}…`;
}

function safeTime(ts: number | null | undefined) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleTimeString();
  } catch {
    return "—";
  }
}

/* ------------------------------------------------------------
 * Drawer (detail surface)
 * ---------------------------------------------------------- */

type DrawerPayload =
  | { kind: "capability"; id: string; data: any }
  | { kind: "violation"; id: string; data: any }
  | { kind: "flow"; id: string; data: any }
  | { kind: "timeline"; id: string; data: any };

function DetailDrawer(props: {
  open: boolean;
  payload: DrawerPayload | null;
  onClose: () => void;
}) {
  React.useEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props.open, props.onClose]);

  if (!props.open || !props.payload) return null;

  const title =
    props.payload.kind === "capability"
      ? `Capability · ${props.payload.id}`
      : props.payload.kind === "violation"
      ? `Violation · ${props.payload.id}`
      : props.payload.kind === "flow"
      ? `Flow · ${props.payload.id}`
      : `Timeline · ${props.payload.id}`;

  return (
    <div className="fixed inset-0 z-[999]">
      {/* backdrop */}
      <button
        type="button"
        aria-label="Close detail drawer"
        onClick={props.onClose}
        className="absolute inset-0 bg-black/55"
      />
      {/* sheet */}
      <div
        role="dialog"
        aria-modal="true"
        className={[
          "absolute right-3 top-3 bottom-3 w-[min(560px,calc(100vw-24px))]",
          "rounded-2xl border border-white/12 bg-black/40 backdrop-blur-2xl",
          "shadow-[0_30px_120px_rgba(0,0,0,0.65)] overflow-hidden",
          "flex flex-col",
        ].join(" ")}
      >
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="text-sm font-semibold">{title}</div>
          <button
            type="button"
            onClick={props.onClose}
            className="px-2.5 py-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/12 text-xs"
          >
            Close
          </button>
        </div>

        <div className="p-4 flex-1 overflow-auto">
          <pre className="text-[11px] leading-snug text-white/80">
            {JSON.stringify(props.payload.data ?? {}, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------
 * Route
 * ---------------------------------------------------------- */

export default function OpsDashboardRoute(): JSX.Element {
  const [state, setState] = React.useState<OpsDashboardStateV1>(() => getState());

  const [drawer, setDrawer] = React.useState<DrawerPayload | null>(null);

  // Ensure bus is installed even if /ops is outside AppShell.
  React.useEffect(() => {
    const unBus = installOpsStateBus();
    return () => {
      try {
        unBus();
      } catch {}
    };
  }, []);

  // Ensure wiring is installed even if /ops is outside AppShell.
  React.useEffect(() => {
    const un = installWiring();
    return () => {
      try {
        un();
      } catch {}
    };
  }, []);

  // Subscribe to ops state snapshots (bus returns clones; never live reference)
  React.useEffect(() => {
    return subscribe((s) => setState(s));
  }, []);

  const id = state.identity;

  const requestSnapshot = () => {
    try {
      window.dispatchEvent(
        new CustomEvent(SYNK_OPS_SNAPSHOT_REQUEST_EVENT, {
          detail: { v: 1, at: Date.now(), requester: "ops-dashboard" },
        }),
      );
    } catch {}
  };

  const caps = Object.values(state.capabilities ?? {}).sort((a, b) =>
    String(a.id).localeCompare(String(b.id)),
  );

  const violationsLatestFirst = [...(state.violations?.recent ?? [])].slice().reverse();
  const timelineLatestFirst = [...(state.timeline?.events ?? [])].slice().reverse();
  const flowChannels = Object.entries(state.flow?.channels ?? {}).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );

  // TOP summary pills — group by meaning, keep unknown quiet
  const envTone: Tone = id.env === "prod" ? "ok" : id.env === "staging" ? "warn" : "quiet";
  const modeTone: Tone =
    id.mode === "live" ? "ok" : id.mode === "degraded" ? "warn" : id.mode === "safe" ? "warn" : "quiet";

  const flowTone = bandTone(state.flow?.band);
  const latencyTone = bandTone(state.latency?.band);
  const vioTone = bandTone(state.violations?.band);

  // No page scroll: lock the route surface
  // (Body-level lock can also live in global CSS; we do it locally here.)
  const rootStyle: React.CSSProperties = { height: "100vh" };

  return (
    <div
      className={[
        "bg-black text-white",
        "overflow-hidden", // ✅ no page scroll
        "relative",
      ].join(" ")}
      style={rootStyle}
    >
      {/* subtle glass gradient */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-100"
        style={{
          background:
            "radial-gradient(1200px 600px at 20% 0%, rgba(56,189,248,0.18), transparent 60%)," +
            "radial-gradient(900px 500px at 80% 20%, rgba(167,139,250,0.14), transparent 65%)," +
            "radial-gradient(900px 500px at 50% 100%, rgba(16,185,129,0.10), transparent 60%)",
        }}
      />

      <div className="relative h-full mx-auto max-w-[1400px] px-4 py-5">
        {/* ---------------- Header (fixed height) ---------------- */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold tracking-tight">Ops Dashboard</h1>
              <Pill tone={modeTone} icon={<IconDot tone={modeTone} />}>
                {id.mode || "unknown"}
              </Pill>
            </div>

            {/* status spine */}
            <div className="flex flex-wrap gap-2">
              {/* Identity cluster */}
              <Pill tone={envTone} title="Environment" icon={<IconDot tone={envTone} />}>
                {id.env}
              </Pill>
              <Pill tone={id.tier === "public" ? "quiet" : "normal"} title="Tier">
                {id.tier}
              </Pill>
              <Pill tone={id.buildId === "unknown" ? "quiet" : "normal"} title="Build id">
                build:{shortId(id.buildId, 12)}
              </Pill>
              <Pill tone={id.manifestChecksum === "unknown" ? "quiet" : "normal"} title="Manifest checksum">
                manifest:{shortId(id.manifestChecksum, 10)}
              </Pill>

              {/* Manifest summary cluster */}
              {id.manifestSummary?.nodes !== undefined && (
                <>
                  <Pill tone="quiet" title="Manifest summary">
                    nodes:{id.manifestSummary.nodes}
                  </Pill>
                  <Pill tone="quiet" title="Manifest summary">
                    routes:{id.manifestSummary.routes}
                  </Pill>
                  <Pill tone="quiet" title="Manifest summary">
                    bridges:{id.manifestSummary.bridges}
                  </Pill>
                  <Pill tone="quiet" title="Manifest summary">
                    checks:{id.manifestSummary.checks}
                  </Pill>
                </>
              )}

              {/* Health cluster */}
              <Pill tone={flowTone} icon={<IconDot tone={flowTone} />} title="Flow band">
                flow:{state.flow?.band ?? "unknown"}
              </Pill>
              <Pill tone={latencyTone} icon={<IconDot tone={latencyTone} />} title="Latency band">
                latency:{state.latency?.band ?? "unknown"}
              </Pill>
              <Pill tone={vioTone} icon={<IconDot tone={vioTone} />} title="Violations band">
                violations:{state.violations?.band ?? "unknown"}
              </Pill>
            </div>
          </div>

          {/* controls */}
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/12 text-sm backdrop-blur-md"
              onClick={requestSnapshot}
              type="button"
            >
              Request snapshot
            </button>
          </div>
        </div>

        {/* ---------------- Main console grid (fills remaining) ---------------- */}
        <div
          className="mt-4 grid gap-4"
          style={{
            // Header consumes ~92px-120px depending on wrap; keep main area stable.
            height: "calc(100% - 128px)",
            gridTemplateRows: "1fr 1fr",
          }}
        >
          {/* Row 1: Capabilities (wide) + Health stack */}
          <div className="grid gap-4" style={{ gridTemplateColumns: "1.6fr 1fr" }}>
            {/* Capabilities (card list, no table) */}
            <Panel
              title={
                <div className="flex items-center gap-2">
                  <span>Capabilities</span>
                  <Pill tone="quiet">count:{caps.length}</Pill>
                </div>
              }
              right={
                <Pill tone="quiet" title="Installed & Active heuristic">
                  active:{caps.filter((c) => !!c.lastSeenAt).length}
                </Pill>
              }
              className="h-full"
            >
              <div className="h-full flex flex-col gap-2">
                <div className="text-xs text-white/60">
                  Tap a capability for details. Page never scrolls; this panel is the list viewport.
                </div>

                <div className="flex-1 overflow-auto rounded-xl border border-white/10 bg-black/20">
                  <div className="p-2 space-y-2">
                    {caps.length === 0 ? (
                      <div className="p-3 text-sm text-white/65">
                        No capabilities reported yet. Confirm installWiring() emits wiring_ready and capability events.
                      </div>
                    ) : (
                      caps.map((c) => {
                        const t = healthTone(c.status);
                        return (
                          <button
                            key={String(c.id)}
                            type="button"
                            onClick={() =>
                              setDrawer({
                                kind: "capability",
                                id: String(c.id),
                                data: c,
                              })
                            }
                            className={[
                              "w-full text-left",
                              "rounded-xl border border-white/10 bg-white/[0.05] hover:bg-white/[0.08]",
                              "px-3 py-2",
                              "transition-colors",
                            ].join(" ")}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-xs font-mono text-white/85 truncate">
                                  {String(c.id)}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-2">
                                  <Pill tone={t} icon={<IconDot tone={t} />}>
                                    {c.status}
                                  </Pill>
                                  <Pill tone="quiet">{c.safety}</Pill>
                                  <Pill tone="quiet">{c.tierRequired}</Pill>
                                  {c.disabledByOps ? <Pill tone="warn">disabledByOps</Pill> : null}
                                </div>
                              </div>

                              <div className="shrink-0 text-right">
                                <div className="text-xs text-white/60">last</div>
                                <div className="text-xs text-white/80">{safeTime(c.lastSeenAt)}</div>
                              </div>
                            </div>

                            <div className="mt-2 text-[11px] text-white/55">
                              reason: {c.reason ?? "—"}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </Panel>

            {/* Health stack: Violations + Flow (mini-panels) */}
            <div className="grid gap-4" style={{ gridTemplateRows: "1fr 1fr" }}>
              <Panel
                title="Violations"
                right={
                  <Pill tone={vioTone} icon={<IconDot tone={vioTone} />}>
                    {state.violations?.band ?? "unknown"}
                  </Pill>
                }
                className="h-full"
              >
                <div className="h-full flex flex-col gap-2">
                  {violationsLatestFirst.length === 0 ? (
                    <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/20 p-3">
                      <div className="text-sm text-white/70">No violations reported.</div>
                      <Pill tone="ok" icon={<IconDot tone="ok" />}>
                        clean
                      </Pill>
                    </div>
                  ) : (
                    <div className="flex-1 overflow-auto rounded-xl border border-white/10 bg-black/20 p-2 space-y-2">
                      {violationsLatestFirst.slice(0, 20).map((v, idx) => {
                        const vt: Tone =
                          v.severity === "critical" ? "crit" : v.severity === "warning" ? "warn" : "quiet";
                        const key = `${v.at}-${idx}`;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() =>
                              setDrawer({
                                kind: "violation",
                                id: key,
                                data: v,
                              })
                            }
                            className="w-full text-left rounded-xl border border-white/10 bg-white/[0.05] hover:bg-white/[0.08] px-3 py-2"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0 text-xs font-mono truncate text-white/85">{v.channel}</div>
                              <Pill tone={vt} icon={<IconDot tone={vt} />}>
                                {v.severity}
                              </Pill>
                            </div>
                            <div className="mt-1 text-[11px] text-white/60">
                              {v.reason} · where:{v.where}{v.schema ? ` · schema:${v.schema}` : ""} · {safeTime(v.at)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Panel>

              <Panel
                title="Flow"
                right={
                  <Pill tone={flowTone} icon={<IconDot tone={flowTone} />}>
                    {state.flow?.band ?? "unknown"}
                  </Pill>
                }
                className="h-full"
              >
                <div className="h-full flex flex-col gap-2">
                  {flowChannels.length === 0 ? (
                    <div className="text-sm text-white/70">No flow metrics reported.</div>
                  ) : (
                    <div className="flex-1 overflow-auto rounded-xl border border-white/10 bg-black/20 p-2 space-y-2">
                      {flowChannels.slice(0, 18).map(([ch, info]) => {
                        const w10 = (info.windows ?? []).find((w) => w.windowSec === 10);
                        const dropped = w10?.dropped ?? 0;
                        const ft: Tone = dropped > 10 ? "crit" : dropped > 0 ? "warn" : "ok";
                        return (
                          <button
                            key={ch}
                            type="button"
                            onClick={() =>
                              setDrawer({
                                kind: "flow",
                                id: ch,
                                data: { channel: ch, info },
                              })
                            }
                            className="w-full text-left rounded-xl border border-white/10 bg-white/[0.05] hover:bg-white/[0.08] px-3 py-2"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0 text-xs font-mono truncate text-white/85">{ch}</div>
                              <Pill tone={ft} icon={<IconDot tone={ft} />}>
                                10s:{w10 ? w10.count : "—"} · drop:{dropped}
                              </Pill>
                            </div>
                            <div className="mt-1 text-[11px] text-white/60">
                              last: {safeTime(info.lastAt)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Panel>
            </div>
          </div>

          {/* Row 2: Timeline viewport + Latency mini panel */}
          <div className="grid gap-4" style={{ gridTemplateColumns: "1.6fr 1fr" }}>
            <Panel
              title={
                <div className="flex items-center gap-2">
                  <span>Timeline</span>
                  <Pill tone="quiet">latest first</Pill>
                </div>
              }
              right={<Pill tone="quiet">events:{timelineLatestFirst.length}</Pill>}
              className="h-full"
            >
              <div className="h-full flex flex-col gap-2">
                <div className="text-xs text-white/60">
                  Compact rows. Click to open JSON in the right drawer.
                </div>

                <div className="flex-1 overflow-auto rounded-xl border border-white/10 bg-black/20">
                  {timelineLatestFirst.length === 0 ? (
                    <div className="p-3 text-sm text-white/65">No timeline events yet.</div>
                  ) : (
                    <div className="p-2 space-y-2">
                      {timelineLatestFirst.slice(0, 40).map((t, idx) => {
                        const key = `${t.at}-${idx}`;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() =>
                              setDrawer({
                                kind: "timeline",
                                id: key,
                                data: t,
                              })
                            }
                            className="w-full text-left rounded-xl border border-white/10 bg-white/[0.05] hover:bg-white/[0.08] px-3 py-2"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-xs font-mono text-white/85 truncate">{t.kind}</div>
                                <div className="mt-1 text-[11px] text-white/60 truncate">
                                  {/* small preview (no big JSON blocks) */}
                                  {t.detail ? Object.keys(t.detail).slice(0, 4).join(", ") || "detail" : "detail"}
                                </div>
                              </div>
                              <div className="shrink-0 text-xs text-white/70">{safeTime(t.at)}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </Panel>

            <Panel
              title="Latency"
              right={
                <Pill tone={latencyTone} icon={<IconDot tone={latencyTone} />}>
                  {state.latency?.band ?? "unknown"}
                </Pill>
              }
              className="h-full"
            >
              <div className="h-full flex flex-col gap-3">
                {(state.latency?.metrics?.length ?? 0) === 0 ? (
                  <div className="text-sm text-white/70">No latency metrics reported.</div>
                ) : (
                  <div className="flex-1 overflow-auto rounded-xl border border-white/10 bg-black/20 p-2 space-y-2">
                    {(state.latency?.metrics ?? []).slice(0, 10).map((m) => {
                      const bt = bandTone(m.band);
                      return (
                        <div
                          key={m.name}
                          className="rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-xs font-mono text-white/85 truncate">{m.name}</div>
                            <Pill tone={bt} icon={<IconDot tone={bt} />}>
                              {m.band}
                            </Pill>
                          </div>
                          <div className="mt-1 text-[11px] text-white/60">
                            p95: {typeof m.p95Ms === "number" ? `${Math.round(m.p95Ms)}ms` : "—"} · last:{safeTime(m.lastAt)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {(state.latency?.degradedReasons?.length ?? 0) > 0 ? (
                  <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                    <div className="text-xs font-semibold text-white/80">degraded reasons</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {state.latency.degradedReasons.slice(0, 8).map((r) => (
                        <Pill key={r} tone="warn">
                          {r}
                        </Pill>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </Panel>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      <DetailDrawer
        open={!!drawer}
        payload={drawer}
        onClose={() => setDrawer(null)}
      />
    </div>
  );
}
